import { ILegalClientDocumentType } from '../../../Interfaces/ILegalClientDocumentType';
import {
  ILegalClientDocumentFile,
  ILegalClientDocumentTypeEntry,
} from '../../../Interfaces/ILegalClientDocumentVersion';
import {
  GWG_REQUIREMENT_FOLDER_DEFAULT,
  IGwGFolder,
  IGwGFolderRequirement,
  IGwGFolderSaveObject,
  IGwGReminder,
  getNextTempId,
  toRequirementArray,
} from '../../../Interfaces/IGwGFolder';

/**
 * Änderungen am GwG-Ordner rein rechnerisch — ohne React, ohne Netzwerk.
 *
 * Es gibt genau eine Quelle der Wahrheit: das **Speicherobjekt**
 * (`IGwGFolderSaveObject`) mit Konfiguration, vorgemerkten Uploads und
 * vorgemerkten Löschungen. Es geht beim Speichern des Vorgangs mit — die
 * Konfiguration darin ist autoritativ, Dokumente sind additiv (Upload) bzw.
 * explizit (Löschung), weil der Ordner nur den GwG-Ausschnitt der
 * Mandanten-Dokumente sieht und nichts darüber hinaus anfassen darf.
 *
 * Die **Sicht** (`IGwGFolder`) wird daraus abgeleitet, nicht mitgeführt:
 * `buildFolderView()` rechnet sie bei jedem Render neu. Ein zweiter,
 * fortgeschriebener Zustand könnte von der Rechnung abweichen, ohne dass es
 * auffällt — deshalb ändern alle Mutationen ausschließlich das Speicherobjekt.
 *
 * Alle Funktionen sind seiteneffektfrei und geben neue Objekte zurück.
 */

export interface IGwGFolderState {
  folder: IGwGFolder;
  saveObject: IGwGFolderSaveObject;
}

/**
 * Was der GwG-Ordner von einer beteiligten juristischen Person braucht.
 *
 * Bewusst ein struktureller Ausschnitt statt `ILegalClientFullObject`:
 * `ILegalTransactionFullObject.LegalClientArray` ist als `ILegalClient[]`
 * deklariert, obwohl das Backend dort vollständige Mandanten-Objekte samt
 * `DocumentTypeArray` liefert (`getLegalTransaction.py` ruft
 * `legalClientDriver.getFullObjectById`). Solange die Deklaration nicht
 * nachgezogen ist, wäre jede engere Typangabe hier eine Lüge in die eine oder
 * andere Richtung — deshalb `DocumentTypeArray` optional und defensiv gelesen.
 */
export interface IGwGLegalPerson {
  idLegalClient: number;
  LastName?: string | null;
  DocumentTypeArray?: ILegalClientDocumentTypeEntry[];
}

/**
 * Sicht aus den lokalen Angaben aufbauen — ohne auf das Backend zu warten.
 *
 * Die Unterlagen stecken bereits im Vorgangs-Payload: `LegalClientArray` enthält
 * vollständige Mandanten-Objekte samt `DocumentTypeArray`
 * (`Dokumenttyp → Version → Dateien`). Damit lässt sich dieselbe Sicht rechnen,
 * die das Backend nach dem Speichern liefert — der GwG-Ordner zeigt seine
 * Dokumente also sofort, wie jeder andere Bereich des Vorgangs auch.
 *
 * Bewusst dieselbe Rechnung wie `gwg.resolveGwGFolderArray()` im Backend: dort
 * ist sie die Autorität (Erinnerungs-Versand, Vollständigkeit), hier nur die
 * Anzeige bis zum Speichern. Optionale Dokumente zählen nicht mit.
 */
export const buildFolderView = (
  idDocumentDirectory: number,
  title: string | null | undefined,
  saveObject: IGwGFolderSaveObject,
  legalPersonArray: IGwGLegalPerson[],
  documentTypeArray: ILegalClientDocumentType[]
): IGwGFolder => {
  const requirementArray = saveObject.RequirementArray;
  const defaultArray = requirementArray.filter(
    requirement =>
      requirement.LegalClient_idLegalClient === GWG_REQUIREMENT_FOLDER_DEFAULT
  );
  const typeNameById = new Map(
    documentTypeArray.map(documentType => [
      documentType.idLegalClientDocumentType,
      documentType.LegalClientDocumentType,
    ])
  );

  const documentTypeViewArray = defaultArray.map(defaultRequirement => {
    const idType = defaultRequirement.idLegalClientDocumentType;
    let openCount = 0;

    const participantArray = legalPersonArray.map(legalClient => {
      const override = requirementArray.find(
        requirement =>
          requirement.idLegalClientDocumentType === idType &&
          requirement.LegalClient_idLegalClient === legalClient.idLegalClient
      );
      const effective = override ?? defaultRequirement;
      const typeEntry = (legalClient.DocumentTypeArray ?? []).find(
        entry => entry.idLegalClientDocumentType === idType
      );

      /**
       * Gespeicherte Versionen, um die zum Löschen vorgemerkten bereinigt und um
       * die noch nicht gespeicherten Uploads ergänzt. Ohne das würde ein Upload
       * beim nächsten Neurechnen der Sicht wieder verschwinden.
       */
      const storedArray = (typeEntry?.VersionArray ?? []).filter(
        version =>
          version.idLegalClientDocumentVersion === null ||
          !saveObject.DeleteVersionIdArray.includes(
            version.idLegalClientDocumentVersion
          )
      );
      const pendingArray = saveObject.NewVersionArray.filter(
        pending =>
          pending.idLegalClient === legalClient.idLegalClient &&
          pending.idLegalClientDocumentType === idType
      );
      const highestVersionNumber = Math.max(
        0,
        ...storedArray.map(version => version.VersionNumber)
      );
      const versionArray = [
        ...pendingArray
          .map((pending, index) => ({
            idLegalClientDocumentVersion: -(index + 1),
            idLegalClientDocumentType: idType,
            VersionNumber: highestVersionNumber + index + 1,
            Created_at: null,
            idUser: null,
            ValidUntil: pending.ValidUntil ?? null,
            Note: pending.Note ?? null,
            isCurrent: false,
            FileArray: pending.FileArray,
          }))
          .reverse(),
        ...storedArray,
      ].map((version, index) => ({ ...version, isCurrent: index === 0 }));

      const currentVersion = versionArray[0] ?? null;
      const hasDocument =
        currentVersion !== null && currentVersion.FileArray.length > 0;

      if (effective.isMandatory && !hasDocument) {
        openCount += 1;
      }

      return {
        idLegalClient: legalClient.idLegalClient,
        LegalClient: legalClient.LastName?.trim() || 'Unbekannte Organisation',
        idGwGFolderRequirement: effective.idGwGFolderRequirement,
        isMandatory: effective.isMandatory,
        hasOverride: override !== undefined,
        hasDocument: hasDocument,
        CurrentVersion: currentVersion,
        VersionArray: versionArray,
        ReminderArray: effective.ReminderArray,
      };
    });

    return {
      idLegalClientDocumentType: idType,
      LegalClientDocumentType:
        typeNameById.get(idType) ?? 'Unbekannte Dokumentart',
      idGwGFolderRequirement: defaultRequirement.idGwGFolderRequirement,
      isMandatoryDefault: defaultRequirement.isMandatory,
      ReminderArray: defaultRequirement.ReminderArray,
      ParticipantArray: participantArray,
      OpenCount: openCount,
      isFulfilled: openCount === 0,
    };
  });

  const mandatoryTotal = documentTypeViewArray.reduce(
    (total, typeEntry) =>
      total +
      typeEntry.ParticipantArray.filter(participant => participant.isMandatory)
        .length,
    0
  );
  const mandatoryFulfilled = documentTypeViewArray.reduce(
    (total, typeEntry) =>
      total +
      typeEntry.ParticipantArray.filter(
        participant => participant.isMandatory && participant.hasDocument
      ).length,
    0
  );

  return {
    idDocumentDirectory: idDocumentDirectory,
    Title: title,
    DocumentTypeArray: documentTypeViewArray,
    Completeness: {
      mandatoryTotal: mandatoryTotal,
      mandatoryFulfilled: mandatoryFulfilled,
      missing: mandatoryTotal - mandatoryFulfilled,
      isComplete: mandatoryTotal === mandatoryFulfilled,
    },
  };
};

/** Ausgangszustand aus der vom Backend gelieferten Sicht. */
export const createState = (folder: IGwGFolder): IGwGFolderState => ({
  folder: folder,
  saveObject: {
    idDocumentDirectory: folder.idDocumentDirectory,
    RequirementArray: toRequirementArray(folder),
    NewVersionArray: [],
    DeleteVersionIdArray: [],
  },
});

/** Hat der Benutzer an diesem Ordner etwas geändert? */
/**
 * Anforderung ändern — arbeitet ausschließlich am Speicherobjekt.
 *
 * Die Anzeige wird nicht mitgeführt: sie entsteht bei jedem Render neu über
 * `buildFolderView()` aus Mandanten-Dokumenten, Konfiguration und Vormerkungen.
 * Ein zweiter, fortgeschriebener Zustand wäre eine Fehlerquelle — er könnte von
 * der berechneten Sicht abweichen, ohne dass es auffällt.
 */
const withRequirement = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  legalClientId: number,
  change: (requirement: IGwGFolderRequirement) => IGwGFolderRequirement
): IGwGFolderSaveObject => {
  const existing = saveObject.RequirementArray.find(
    requirement =>
      requirement.idLegalClientDocumentType === idLegalClientDocumentType &&
      requirement.LegalClient_idLegalClient === legalClientId
  );

  if (existing !== undefined) {
    return {
      ...saveObject,
      RequirementArray: saveObject.RequirementArray.map(requirement =>
        requirement === existing ? change(requirement) : requirement
      ),
    };
  }

  /**
   * Noch keine Zeile für diesen Beteiligten: ein neuer Override entsteht. Er
   * übernimmt den Standard des Ordners als Ausgangswert — sonst würde das
   * Anlegen einer Ausnahme die Erinnerungen stillschweigend verwerfen (eine
   * Override-Zeile ist im Backend vollständig autoritativ).
   */
  const folderDefault = saveObject.RequirementArray.find(
    requirement =>
      requirement.idLegalClientDocumentType === idLegalClientDocumentType &&
      requirement.LegalClient_idLegalClient === GWG_REQUIREMENT_FOLDER_DEFAULT
  );

  const created: IGwGFolderRequirement = {
    idGwGFolderRequirement: getNextTempId(
      saveObject.RequirementArray.map(
        requirement => requirement.idGwGFolderRequirement
      )
    ),
    idLegalClientDocumentType: idLegalClientDocumentType,
    LegalClient_idLegalClient: legalClientId,
    isMandatory: folderDefault?.isMandatory ?? true,
    /**
     * Fortlaufend negative IDs und nicht überall `-1`: die Liste braucht
     * eindeutige Schlüssel. Mit mehrfach vergebener `-1` waren die Zeilen im
     * Erinnerungsdialog nicht auseinanderzuhalten, und das Ändern einer traf
     * alle gleichnamigen mit (QS #23, Punkt 6).
     */
    ReminderArray: (folderDefault?.ReminderArray ?? []).map(
      (reminder, index) => ({
        ...reminder,
        idGwGReminder: -1 - index,
        idGwGFolderRequirement: null,
        SentFor: null,
      })
    ),
  };

  return {
    ...saveObject,
    RequirementArray: [...saveObject.RequirementArray, change(created)],
  };
};

/** Pflicht-/Optional-Status des Ordner-Standards einer Dokumentart setzen. */
export const setDefaultMandatory = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  isMandatory: boolean
): IGwGFolderSaveObject =>
  withRequirement(
    saveObject,
    idLegalClientDocumentType,
    GWG_REQUIREMENT_FOLDER_DEFAULT,
    requirement => ({ ...requirement, isMandatory: isMandatory })
  );

/** Pflicht-/Optional-Status für einen einzelnen Beteiligten überschreiben. */
export const setParticipantMandatory = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  idLegalClient: number,
  isMandatory: boolean
): IGwGFolderSaveObject =>
  withRequirement(
    saveObject,
    idLegalClientDocumentType,
    idLegalClient,
    requirement => ({ ...requirement, isMandatory: isMandatory })
  );

/** Ausnahme eines Beteiligten aufheben — der Ordner-Standard gilt wieder. */
export const clearParticipantOverride = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  idLegalClient: number
): IGwGFolderSaveObject => ({
  ...saveObject,
  RequirementArray: saveObject.RequirementArray.filter(
    requirement =>
      !(
        requirement.idLegalClientDocumentType === idLegalClientDocumentType &&
        requirement.LegalClient_idLegalClient === idLegalClient
      )
  ),
});

/**
 * Erinnerungen einer Anforderung ersetzen. `idLegalClient = 0` betrifft den
 * Ordner-Standard, jeder andere Wert die Ausnahme dieses Beteiligten.
 */
export const setReminderArray = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  idLegalClient: number,
  reminderArray: IGwGReminder[]
): IGwGFolderSaveObject =>
  withRequirement(
    saveObject,
    idLegalClientDocumentType,
    idLegalClient,
    requirement => ({ ...requirement, ReminderArray: reminderArray })
  );

/**
 * Neue Dokumentversion vormerken. Sie landet beim Speichern beim jeweiligen
 * Mandanten; in der Sicht erscheint sie sofort, weil `buildFolderView()` die
 * Vormerkungen mit einrechnet.
 */
export const addVersion = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  idLegalClient: number,
  fileArray: ILegalClientDocumentFile[],
  validUntil: string,
  note: string
): IGwGFolderSaveObject => ({
  ...saveObject,
  NewVersionArray: [
    ...saveObject.NewVersionArray,
    {
      idLegalClient: idLegalClient,
      idLegalClientDocumentType: idLegalClientDocumentType,
      FileArray: fileArray,
      ValidUntil: validUntil === '' ? null : validUntil,
      Note: note === '' ? null : note,
    },
  ],
});

/**
 * Version entfernen. Eine noch nicht gespeicherte Version (negative ID) wird nur
 * aus der Vormerkung genommen, eine bestehende explizit zum Löschen angemeldet.
 */
export const removeVersion = (
  saveObject: IGwGFolderSaveObject,
  idLegalClientDocumentType: number,
  idLegalClient: number,
  idLegalClientDocumentVersion: number | null
): IGwGFolderSaveObject => {
  const isPending =
    idLegalClientDocumentVersion === null || idLegalClientDocumentVersion < 0;

  if (isPending) {
    /**
     * Die zuletzt vorgemerkte Version dieses Beteiligten und Typs zurücknehmen —
     * mehr Zuordnung braucht es nicht, weil eine vorgemerkte Version keine echte
     * ID hat und die Reihenfolge der Vormerkungen erhalten bleibt.
     */
    const lastIndex = saveObject.NewVersionArray.map((entry, index) => ({
      entry,
      index,
    }))
      .filter(
        ({ entry }) =>
          entry.idLegalClient === idLegalClient &&
          entry.idLegalClientDocumentType === idLegalClientDocumentType
      )
      .map(({ index }) => index)
      .pop();

    return {
      ...saveObject,
      NewVersionArray:
        lastIndex === undefined
          ? saveObject.NewVersionArray
          : saveObject.NewVersionArray.filter(
              (entry, index) => index !== lastIndex
            ),
    };
  }

  return {
    ...saveObject,
    DeleteVersionIdArray: [
      ...saveObject.DeleteVersionIdArray,
      idLegalClientDocumentVersion as number,
    ],
  };
};

/** Vollständigkeitsstatus als Satz, wie er über der Ansicht steht. */
export const describeCompleteness = (folder: IGwGFolder): string => {
  const { mandatoryTotal, mandatoryFulfilled, missing } = folder.Completeness;

  if (mandatoryTotal === 0) {
    return 'Keine Pflichtunterlagen festgelegt.';
  }

  if (missing === 0) {
    return `GwG-Prüfung: alle ${mandatoryTotal} Pflichtunterlagen vorhanden`;
  }

  return `GwG-Prüfung: ${mandatoryFulfilled} von ${mandatoryTotal} Pflichtunterlagen vorhanden — ${
    missing === 1
      ? '1 Pflichtunterlage fehlt'
      : `${missing} Pflichtunterlagen fehlen`
  }`;
};
